/**
 * 에셋 주소를 **상대 경로로 저장한다** — 운영자 PC 주소가 게시본에 박히지 않게.
 *
 * ## 왜 이게 문제인가
 * 관리자에서 그림을 올리면 엣지가 R2 에 넣고 주소를 돌려준다. 예전엔 그게 **절대 URL** 이었다:
 * `http://localhost:8788/assets/…`. 운영자가 자기 PC 에서 작업하니 당연히 자기 주소가 붙는다.
 * 그 주소가 그대로 게시본(D1)에 저장된다.
 *
 * 실측: 게시본 232개 중 **147개가 localhost** 를 가리키고 있었다(63%).
 * 이게 프로덕션에 들어가면 아이 화면의 그림 대부분이 깨진다 — 그런데 **운영자 PC 에서는
 * 멀쩡히 보인다.** 자기 주소니까. 그래서 배포 전에는 절대 알 수 없다.
 *
 * ## 그래서 상대 경로로 저장한다
 * 운영에서는 웹과 엣지가 **같은 출처**(`woorinabiglobal.com`)라 `/assets/…` 하나로 끝난다(불변식 2).
 * dev 에서만 웹(:5173)·관리자(:5174)가 엣지(:8788)와 출처가 달라서, **읽는 쪽이** 그때만
 * 앞에 엣지 주소를 붙인다. 저장된 값에는 어떤 호스트도 안 들어간다.
 */

/** R2 에 올린 에셋 경로 접두사. 엣지가 이 경로로 서빙한다(`handleAssetGet`). */
export const EDGE_ASSET_PREFIX = "/assets/";

/** 엣지가 서빙하는 에셋 경로인가(상대). */
export function isEdgeAssetPath(url: string | undefined): boolean {
  return !!url && url.startsWith(EDGE_ASSET_PREFIX);
}

/**
 * 읽을 때: 상대 경로 → 지금 브라우저가 실제로 받을 수 있는 주소.
 * `base` 가 빈 문자열이면(운영, 동일 출처) 그대로 둔다.
 */
export function resolveEdgeAsset(url: string | undefined, base: string): string | undefined {
  if (!url || !base || !isEdgeAssetPath(url)) return url;
  return `${base}${url}`;
}

/**
 * 저장할 때: 어떤 형태로 들어와도 `/assets/…` 만 남긴다.
 *
 * 절대 URL(`http://localhost:8788/assets/x.webp`)·프로토콜 상대(`//host/assets/…`) 모두
 * 경로만 남긴다. **호스트를 판별해서 지우는 게 아니라** `/assets/` 부터 잘라낸다 —
 * 운영자 PC 주소가 localhost·127.0.0.1·LAN IP·ngrok 무엇이든 될 수 있어서,
 * "아는 호스트만 지우기"는 반드시 새는 목록이 된다.
 */
export function toRelativeEdgeAsset(url: string | undefined): string | undefined {
  if (!url || isEdgeAssetPath(url)) return url;
  const i = url.indexOf(EDGE_ASSET_PREFIX);
  if (i <= 0) return url; // 없거나 이미 0번(=상대)
  // `/assets/` 앞이 출처처럼 보이는 것만 자른다(경로 중간의 우연한 일치 방지)
  const head = url.slice(0, i);
  return /^(https?:)?\/\/[^/]+$/.test(head) ? url.slice(i) : url;
}

/** 게시 직전 매니페스트 전체 정규화. url·prev 둘 다. */
export function relativizeManifestAssets<T extends { url?: unknown; prev?: unknown }>(
  assets: Record<string, T>,
): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [k, e] of Object.entries(assets)) {
    if (!e || typeof e !== "object") {
      out[k] = e;
      continue;
    }
    const next = { ...e } as T & { url?: unknown; prev?: unknown };
    if (typeof e.url === "string") next.url = toRelativeEdgeAsset(e.url);
    if (typeof e.prev === "string") next.prev = toRelativeEdgeAsset(e.prev);
    out[k] = next;
  }
  return out;
}

/**
 * 배포하면 안 되는 주소인가 — 운영자 PC·사설망을 가리키는 절대 URL.
 *
 * 정규화가 뚫렸을 때를 위한 **마지막 그물**이다(`pnpm check:deploy`). 여기서 걸리면
 * 배포를 막는다 — 아이 화면이 깨지는 걸 배포 후에 알게 되는 것보다 낫다.
 */
export function isLocalOnlyUrl(url: string | undefined): boolean {
  if (!url) return false;
  return /^(https?:)?\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\]|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/i.test(
    url,
  );
}
